/**
 * ToolNavBar — persistent secondary navigation shown on every tool page
 * so operators are never stranded without a way back to the rest of the
 * site. History-aware Back button + shortcuts to sibling tools + the
 * calculators index.
 *
 * The tool that renders this passes `activeSlug` so the tab for the
 * current tool gets highlighted (or is hidden entirely — we don't want
 * a "back to Label Maker" link ON the Label Maker page).
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  ArrowLeft, Home, Calculator, UtensilsCrossed, Tag, BookOpen,
} from 'lucide-react';

const TOOL_LINKS = [
  { slug: 'calculators',       label: 'All tools',       path: '/calculators',        Icon: Calculator },
  { slug: 'food-prep',         label: 'Home Meal Prep',  path: '/food-prep-resource', Icon: UtensilsCrossed },
  { slug: 'label-maker',       label: 'Label Maker',     path: '/label-maker',        Icon: Tag },
  { slug: 'recipe-center',     label: 'Recipe Book',     path: '/recipe-center',      Icon: BookOpen },
];

export default function ToolNavBar({ activeSlug = '' }) {
  const navigate = useNavigate();
  return (
    <div
      className="flex flex-wrap items-center gap-2 no-print mb-4"
      data-testid="tool-nav-bar"
    >
      <Button
        type="button" size="sm" variant="outline"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/calculators'))}
        data-testid="tool-nav-back"
      >
        <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back
      </Button>
      <Button
        type="button" size="sm" variant="ghost"
        onClick={() => navigate('/')}
        data-testid="tool-nav-home"
      >
        <Home className="w-3.5 h-3.5 mr-1" /> Dashboard
      </Button>
      {TOOL_LINKS.filter((t) => t.slug !== activeSlug).map(({ slug, label, path, Icon }) => (
        <Button
          key={slug}
          type="button" size="sm" variant="ghost"
          onClick={() => navigate(path)}
          data-testid={`tool-nav-${slug}`}
        >
          <Icon className="w-3.5 h-3.5 mr-1" /> {label}
        </Button>
      ))}
    </div>
  );
}
